import type { SessionPolicy, SessionPolicyRow } from '@ctcells/api'
import { errorMessage, t, type ErrorLike } from '@ctcells/i18n'

import { rejectReasons } from './alarmRecipientsModel.ts'

/**
 * 관리 › 설정 정책 셋의 순수 계산 — 화면이 판정하지 않는 범위 안에서 «보이는 모양»만 짓는다.
 *
 * 🔴 실효 값 판정(계정 → 역할 → 전체 → 기본값)은 서버 몫이다(`GET /users/{id}/session-policy`). 여기서 단을 타고 올라가지 않는다.
 * 🔴 무인 사이클 경고는 **서버가 그 비교를 화면에 맡긴 것**이다 — 계약 `SessionPolicy.unattended_cycle_sec` 설명
 *    「절대 상한이 이보다 짧으면 화면이 경고한다」. 그래서 **그 단에 적힌 값**만 비교한다(물려받은 값을 짓지 않는다).
 */

/**
 * 관리 › 사용자 상세의 「세션 만료」 카드(계정 단 액세스 토큰 · 무활동 만료 · 절대 상한)를 보이는가 — 숨김(eric 2026-10-29 「사용자에서..
 * 이거 세션 만료 이거 빼줘」). 서버 설정을 바꾸는 관리 기능이라 코드는 남기고 이 스위치 한 곳으로 숨긴다(테마색 `ACCENT_PICKER_SHOWN` 과 같은 꼴).
 * 🔴 숨은 동안은 그 카드만 쓰던 조회(`GET /users/{id}/session-policy`)도 부르지 않는다(`AdminContainer` · 요청이 헛돌지 않게).
 * 되살리려면 `true` 로 — 카드 · 조회 · 단 넣기가 함께 돌아온다. 관리 › 설정의 세션 정책 화면은 이 스위치와 무관하다.
 */
export const USER_SESSION_POLICY_SHOWN: boolean = false

/** 세션 단 한 줄 — 서버 행이 없으면 세 칸이 `null`(그 단은 안 정함) */
export interface SessionTier {
  scope: string
  access_ttl_sec: number | null
  idle_ttl_sec: number | null
  absolute_ttl_sec: number | null
  /** 서버 행이 있을 때만 */
  row?: SessionPolicyRow | undefined
}

export const SESSION_FIELDS = ['access_ttl_sec', 'idle_ttl_sec', 'absolute_ttl_sec'] as const
export type SessionField = (typeof SESSION_FIELDS)[number]

/**
 * 표의 줄 — 전체(`default`) · 역할마다(`roles` 전부 · 행이 없어도 고칠 수 있게) · 서버가 준 나머지 행(계정 단) 차례.
 * ⚠ 계정 단은 **서버에 행이 있는 것만** 선다. 새 계정 단은 계정 상세에서 만든다(`07` §4.7 ⓑ).
 */
export function sessionTiers(policy: SessionPolicy): SessionTier[] {
  const byScope = new Map(policy.rows.map((row) => [row.scope, row]))
  const scopes = ['default', ...policy.roles.map((role) => `role:${role}`)]
  for (const row of policy.rows) if (!scopes.includes(row.scope)) scopes.push(row.scope)
  return scopes.map((scope) => {
    const row = byScope.get(scope)
    return {
      scope,
      access_ttl_sec: row?.access_ttl_sec ?? null,
      idle_ttl_sec: row?.idle_ttl_sec ?? null,
      absolute_ttl_sec: row?.absolute_ttl_sec ?? null,
      ...(row === undefined ? {} : { row }),
    }
  })
}

/** 단 표기 — `default` → 전체 · `role:x` → 역할 · x · `user:id` → 계정 · id. 모르는 모양은 원문 */
export function scopeLabel(scope: string): string {
  if (scope === 'default') return t('policy.scope.default')
  const [kind, ...rest] = scope.split(':')
  const id = rest.join(':')
  if (kind === 'role' && id !== '') return t('policy.scope.role', { id })
  if (kind === 'user' && id !== '') return t('policy.scope.user', { id })
  return scope
}

/** 그 단에 «적힌» 절대 상한이 무인 사이클보다 짧은 단들(기본값 포함). 물려받은 값은 보지 않는다 */
export function belowCycleScopes(policy: SessionPolicy): string[] {
  const cycle = policy.unattended_cycle_sec
  const hits: string[] = []
  if (policy.defaults.absolute_ttl_sec < cycle) hits.push(t('policy.scope.base'))
  for (const row of policy.rows) {
    if (row.absolute_ttl_sec != null && row.absolute_ttl_sec < cycle)
      hits.push(scopeLabel(row.scope))
  }
  return hits
}

/** 서버 단위 값 → 입력칸 글자(소수 둘째 자리까지 · 끝의 0 은 뺀다) */
export function toInputText(value: number | null, scale: number): string {
  if (value === null) return ''
  return String(Math.round((value / scale) * 100) / 100)
}

/**
 * 입력칸 글자 → 서버 단위 값.
 * - 빈 칸 → `null`(지움 — 윗단 · 기본값을 따른다)
 * - 1 이상의 정수(서버 단위)가 아니면 `'invalid'` — 계약 `minimum: 1` · 정수라 보내기 전에 막는다
 */
export function fromInputText(text: string, scale: number): number | null | 'invalid' {
  const trimmed = text.trim()
  if (trimmed === '') return null
  const n = Number(trimmed)
  if (!Number.isFinite(n)) return 'invalid'
  const server = Math.round(n * scale)
  return server >= 1 ? server : 'invalid'
}

/** 거절 문구 — 422 면 서버가 준 까닭(`context.reason`) 그대로 · 아니면 에러 코드 사전 문구 */
export function policyFailure(error: unknown): string {
  const reasons = rejectReasons(error)
  return reasons.length > 0 ? reasons.join(' · ') : errorMessage(error as ErrorLike)
}

/**
 * 초 → 「24시간」 · 「1시간 30분」 · 「15분」. 0 인 단위는 뺀다. 시 · 분 · 초가 다 0 이면 「0초」.
 * ⚠ 날(day) 단위로 올리지 않는다 — 「절대 상한 24시간」이 정본(`UAM-SES-06`) 표기다.
 */
export function ttlText(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds))
  const h = Math.floor(whole / 3600)
  const m = Math.floor((whole % 3600) / 60)
  const s = whole % 60
  const parts = [
    ...(h > 0 ? [t('userSessionPolicy.unit.hours', { count: h })] : []),
    ...(m > 0 ? [t('userSessionPolicy.unit.minutes', { count: m })] : []),
    ...(s > 0 || (h === 0 && m === 0) ? [t('userSessionPolicy.unit.seconds', { count: s })] : []),
  ]
  return parts.join(' ')
}

/** 본문의 칸 하나 — PUT 이 병합이라 고친 칸만 싣는다(`null` = 지움) */
export function oneField<K extends string>(
  field: K,
  value: number | null,
): Partial<Record<K, number | null>> {
  const out: Partial<Record<K, number | null>> = {}
  out[field] = value
  return out
}
